export type EmbedProvider = "youtube" | "loom";

export type EmbedTarget = { provider: EmbedProvider; src: string };

/** Hosts an embed iframe may load from. The server sanitiser enforces the same list. */
export const EMBED_IFRAME_HOSTS = ["www.youtube-nocookie.com", "www.loom.com"] as const;

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;
const LOOM_ID = /^[a-f0-9]{32}$/;

/**
 * Turn a pasted YouTube or Loom link into its embed URL, or null when the link is not one we
 * support. Only ids are carried over, so nothing else from the pasted URL reaches the iframe.
 */
export const toEmbedTarget = (input: string): EmbedTarget | null => {
  let url: URL;
  try {
    url = new URL(input.trim());
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;

  const host = url.hostname.replace(/^www\.|^m\./, "");
  const parts = url.pathname.split("/").filter(Boolean);

  if (host === "youtube.com" || host === "youtube-nocookie.com" || host === "youtu.be") {
    let id: string | null | undefined;
    if (host === "youtu.be") id = parts[0];
    else if (parts[0] === "watch") id = url.searchParams.get("v");
    else if (["embed", "shorts", "live"].includes(parts[0] ?? "")) id = parts[1];
    if (!id || !YOUTUBE_ID.test(id)) return null;
    return { provider: "youtube", src: `https://www.youtube-nocookie.com/embed/${id}` };
  }

  if (host === "loom.com" && (parts[0] === "share" || parts[0] === "embed")) {
    const id = parts[1];
    if (!id || !LOOM_ID.test(id)) return null;
    return { provider: "loom", src: `https://www.loom.com/embed/${id}` };
  }

  return null;
};
